// Story 7-FU-K (ADO-1528), AC1/AC2/AC4/AC5/AC6 — ONE OWNER for the substitute-candidate
// availability read cycle.
//
// WHAT THIS IS FOR. A read cycle answers one QUESTION: does this candidate set have stock at
// this depot. Several cards can be asking it at once — a grid where two products offer the same
// substitutes — and they mount at different times. Before this module the cycle was driven by
// each card's own effect in `useCandidateAvailability`, with the shared retry register left to
// reconcile them; every observer called `recordFailure`/`gaveUp`/`status` for itself. That made
// the ceiling a function of the AUDIENCE rather than of the question: the register's
// compare-and-swap counted one shared round once, which kept co-observers in lockstep but
// discarded a lagging observer's report, so an observer that entered mid-ladder put a request
// on the wire that was never counted (4 requests against a ceiling of 3 for two cards ~100ms
// apart; N+2 for overlapping cycles from a band flap). Six rounds of PR #160 moved that
// boundary one step at a time. This module is the boundary itself.
//
// So: exactly ONE driver per question issues attempts, decides terminality and holds the state.
// Consumers SUBSCRIBE — they never touch the register. Three properties fall out rather than
// being enforced separately:
//  - AC2: the ceiling counts REQUESTS PUT ON THE WIRE. `issue` is the single place a request is
//    made and the single place the count is ADDED to, so the count cannot depend on how many
//    observers there are or when they arrived. It is not enforced by a separate comparison at the
//    issue point: a request can only be issued from `drive`, and `drive` is reachable with the
//    ladder spent only via `status()`, which answers `'cooling'` and returns before `issue`.
//  - AC5: `status()` is destructive on expiry, and that is safe because this module is its ONLY
//    reader. Under the old multi-reader arrangement only the FIRST observer saw `'expired'` and
//    re-seated its count; the second was left holding a stale one, which then fed a retry on a
//    backoff rung the ladder did not define. There is nothing to re-seat here.
//  - AC4: terminality IS "the ladder has no rung for the requests already spent"
//    (`nextBackoffMs` returning `undefined`), so a retry can never be scheduled on an
//    undefined rung.
//
// WHAT CANCELLATION DOES AND DOES NOT DO (AC6, and PR #160 round 14). A consumer going away
// suppresses further ISSUING, SCHEDULING and STATE WRITES. It never un-counts a request that
// was put on the wire, and never resets the bound: round 14 measured 8 calls against a ceiling
// of 3 across 5 remounts precisely because the accounting used to depend on someone still
// watching. Charging at ISSUE is what lets that rule and AC6 hold together without either
// yielding — a `'superseded'` outcome is uncharged because NO REQUEST WAS SPENT (or its result
// belongs to a depot we have left), not because a departure waived a charge.
//
// WHAT THE COOLDOWN DOES AND DOES NOT DO (unchanged from 7-FU-I). `RETRY_COOLDOWN_MS` expires
// in the register and nothing reads it on a clock: a cycle that stays observed past the window
// does not re-ladder, by design. The cooldown bounds re-laddering when something RE-ASKS (a
// remount, a band toggle, a depot change) — it is not a recovery schedule, which against a
// hard-down endpoint would be polling. Recovery comes from a read that ANSWERS the candidates:
// a socket push, or the reconnect catch-up. Both clear the error through the store's live
// `checkedSkus`, with no help from this module — which is why AC4 can settle into a QUIET
// terminal error with no manual "Try again" CTA (the PO decision, ADO-1222 OQ-2).

import { createRetryRegister, nextBackoffMs } from '../shared/bounded-retry';
import { useAvailabilityStore, type ReadOutcome } from './useAvailabilityStore';

/**
 * Where a question's read stands.
 *
 * `'pending'` — a request is on the wire, or a backoff is armed, or the cycle has just opened.
 * `'settled'` — the store holds an ANSWER for every candidate (which includes "no stock row at
 *               this depot" — see `checkedSkus`).
 * `'failed'`  — the ladder is spent: the endpoint was asked `MAX_RETRY_ATTEMPTS` times and left
 *               the question unanswered, and the cooldown now refuses a fresh ladder.
 */
export type ReadCycleState = 'pending' | 'settled' | 'failed';

/**
 * The question: this candidate set at this depot. `candidateKey` is `JSON.stringify(skus)` (not
 * `join`) so a SKU containing the delimiter cannot collide two different sets into one key
 * (`['A,B']` vs `['A','B']`); SKUs are numeric today, but nothing enforces that.
 */
export const readCycleKey = (candidateKey: string, depotId: string | null): string =>
  `${candidateKey}|${depotId ?? ''}`;

type Observer = (state: ReadCycleState) => void;

interface Cycle {
  readonly key: string;
  readonly skus: readonly string[];
  readonly depotId: string;
  state: ReadCycleState;
  readonly observers: Set<Observer>;
  /** An armed backoff. Cleared when the last observer leaves — a cancelled cycle must not put a
   *  further request on the wire on behalf of a consumer that is gone. */
  timer: ReturnType<typeof setTimeout> | undefined;
  /** A request of OURS is on the wire. While this holds, nothing else may issue for this
   *  question, and the record survives an unobserve so the response is still accounted. */
  inFlight: boolean;
}

// The bound. Private to this module: `issue` adds to the count, `settle` disposes of it (forget on
// `'answered'`, voidIssue on `'superseded'`, gaveUp at the ceiling), `drive` forgets a question the
// store has answered and is the ONLY caller of `status()` — which is the single-reader property
// AC5 rests on (see this file's header).
const register = createRetryRegister();

// The LIVE cycles: a record exists only while a question is observed, in flight, or laddering.
// Deliberately not the bound — the bound outlives its consumers (that is what stops a remount
// loop) and lives in the register, which is LRU-capped. This map needs no cap of its own: it is
// bounded by the questions currently on screen plus at most one settling request each.
const cycles = new Map<string, Cycle>();

function emit(cycle: Cycle, state: ReadCycleState): void {
  cycle.state = state;
  // Copied: an observer's listener sets React state, and a re-render could unsubscribe a
  // sibling before this loop is done.
  for (const observer of [...cycle.observers]) observer(state);
}

/** Drop a record with nothing left to do. The bound stays in the register. */
function collect(cycle: Cycle): void {
  if (cycle.observers.size > 0 || cycle.inFlight || cycle.timer !== undefined) return;
  if (cycles.get(cycle.key) === cycle) cycles.delete(cycle.key);
}

/** Put one request for `missing` on the wire, counting it BEFORE it goes out. */
function issue(cycle: Cycle, missing: readonly string[]): void {
  const issued = register.recordIssue(cycle.key);
  cycle.inFlight = true;
  // `track: false` — the consumer's refcounted `retainSkus` claim owns the tracking so it can be
  // released on unmount (7-FU-I D-8). This read must not promote the candidates to session
  // interest.
  void useAvailabilityStore
    .getState()
    .loadForSkus(cycle.depotId, missing, { track: false })
    .then(
      (outcome) => {
        cycle.inFlight = false;
        settle(cycle, outcome, issued);
      },
      // `loadForSkus` is not expected to reject — `flushBatch` catches everything and always
      // settles its promise — so this is the "expected impossible" leg, and it is here because
      // `inFlight` is a ONE-WAY LATCH. Without it, one rejection strands the question forever:
      // `drive` returns at its first line on every later subscribe, so no request can ever be
      // issued again and every observing card renders `pending` for the life of the tab, while
      // `collect` refuses to drop the record so the map grows a permanent row. Treated as
      // `'unread'` — a request WAS spent and the question is unanswered, which is exactly what
      // `'unread'` means — so the ladder advances and the cycle can reach a terminal state a
      // consumer can see, instead of hanging.
      //
      // NOT COVERED, and recorded as such (`deferred-work.md`): a promise that NEVER SETTLES.
      // Neither leg runs, so the latch holds on exactly the terms above, and this IS new
      // behaviour — the per-mount effect this module replaced re-entered `attempt()` on every
      // remount and so recovered by accident. It needs a clock, and the fix belongs in the
      // availability TRANSPORT rather than here: `fetchAvailability` has no timeout and no abort,
      // so bounding it there tears the hung connection down and covers the listing's own batch at
      // the same time. `auth0-client.ts:465-484` is the established shape for it in this app —
      // and it documents this same failure (a synchronous in-flight guard latched by a request
      // that connects and never answers, recoverable only by a reload), which is why this is a
      // recorded gap rather than a theoretical one.
      () => {
        cycle.inFlight = false;
        settle(cycle, 'unread', issued);
      },
    );
}

function settle(cycle: Cycle, outcome: ReadOutcome, issued: number): void {
  // A `resetAvailabilityReadCycles()` landed while this was on the wire: the record is retired
  // and its register key already cleared, so writing either now would resurrect a spent cycle.
  // Nothing else removes a record with `inFlight` set, so this identity check is exactly the
  // reset guard.
  if (cycles.get(cycle.key) !== cycle) return;

  if (outcome === 'answered') {
    register.forget(cycle.key); // cycle over — a later failure starts a fresh ladder
    emit(cycle, 'settled');
    collect(cycle);
    return;
  }

  if (outcome === 'superseded') {
    // AC6. The read's answer could not be used — no request was spent (the depot moved before the
    // batch flushed), or one was spent whose result belongs to a depot we have left or to a claim
    // that has since been retired and re-taken. Either way nothing was learned about whether the
    // endpoint can answer, so the budget goes back and no rung of the ladder is climbed. This is
    // NOT "a cancellation waived a charge": a consumer that merely goes away produces no
    // superseded outcome at all, its request completes and is accounted like any other (round 14).
    register.voidIssue(cycle.key);
    // If the question is live again — the user came straight back to this depot, or the retired
    // claim has been re-taken — re-ask IMMEDIATELY. Guarded on the depot rather than driven
    // unconditionally: a question that is no longer the live one must not spin, it must wait to be
    // unobserved.
    //
    // WHY THIS IS NOT RATE-LIMITED, since an uncharged path back into `drive` invites the
    // question. It cannot self-sustain, and the two producers of `'superseded'` are bounded for
    // different reasons:
    //  - a DEPOT switch cannot reach this line at all. A read is superseded only while the store's
    //    depot differs from the batch's, and this re-ask requires it to match again — the two
    //    conditions are mutually exclusive at the moment of settling.
    //  - a RETIRED CLAIM (a remount, or React's StrictMode setup → cleanup → setup) produces
    //    exactly one discard per churn event, and the churn is the consumer mounting and
    //    unmounting. One re-ask per remount is not a loop; it is the recovery, and it is the
    //    ordinary case, which is why it is not delayed — a backoff here would tax every legitimate
    //    remount to defend against something no consumer produces.
    // Measured rather than argued: a run-count cap was tried here and no test could tell it from
    // its absence (the same 2 requests either way under continuous claim churn), which is the
    // evidence that there is nothing for it to bound. Recorded so the next round finds the working
    // rather than re-deriving it.
    if (useAvailabilityStore.getState().depotId === cycle.depotId) drive(cycle);
    collect(cycle);
    return;
  }

  // `'unread'` — a request WAS spent and the endpoint left at least one candidate unanswered. The
  // charge stands whether or not anyone is still watching.
  const delay = nextBackoffMs(issued);
  if (delay === undefined) {
    // The ladder has no rung left: this WAS the terminal request. Stamping the give-up here —
    // at give-up, not at cycle open — is what makes the window mean "N seconds since we last
    // gave up on this question" however slow the attempts were (round 4(d)). Unconditional:
    // stamping only when a consumer survived the response left the bound unsettled by exactly
    // the consumer that goes away mid-flight (round 14).
    register.gaveUp(cycle.key);
    emit(cycle, 'failed');
    collect(cycle);
    return;
  }
  if (cycle.observers.size === 0) {
    // Cancelled. Do not schedule and do not write state — there is nothing to render for and no
    // point retrying on behalf of a consumer that is gone. The accounting above already stands
    // for whoever asks next, and a returning consumer RESUMES this ladder from the register
    // rather than restarting it (round 11, F7).
    collect(cycle);
    return;
  }
  // Stay PENDING through the backoff, so a failure that is about to be retried is never
  // surfaced as one (AC4's quiet recovery).
  cycle.timer = setTimeout(() => {
    cycle.timer = undefined;
    drive(cycle);
  }, delay);
}

/**
 * Decide what this question needs next. The ONLY path to a request. Re-entrant by design: it
 * runs on subscribe, when a backoff fires, and when a superseded read hands its budget back.
 */
function drive(cycle: Cycle): void {
  // Somebody already owns the next step for this question. This is the whole of AC2: an
  // observer arriving while a request is on the wire or a backoff is armed adds an observer,
  // not a request — however offset in time its arrival is.
  if (cycle.inFlight || cycle.timer !== undefined) return;
  if (cycle.observers.size === 0) return; // nothing to issue on behalf of

  // Read the cooldown FIRST: `'expired'` also forgets the key, so the count `issue` is about to
  // add to — and that the ladder is measured against when the response lands — is genuinely fresh
  // rather than inheriting requests the window has just dropped.
  if (register.status(cycle.key) === 'cooling') {
    emit(cycle, 'failed');
    return;
  }

  // Only ask for candidates the store holds no ANSWER for at this depot — the listing's own
  // batch, or another card offering the same substitute, may have established them. Keyed on
  // `checkedSkus`, so a candidate a read established as NOT STOCKED here counts as answered and
  // is not re-asked. Re-evaluated on every pass: a push may have answered them during a backoff.
  const store = useAvailabilityStore.getState();
  const missing =
    store.depotId === cycle.depotId
      ? cycle.skus.filter((sku) => !store.checkedSkus.has(sku))
      : [...cycle.skus];
  if (missing.length === 0) {
    register.forget(cycle.key); // the question is answered — end any open cycle
    emit(cycle, 'settled');
    return;
  }

  issue(cycle, missing);
}

/**
 * Observe the read cycle for `skus` at `depotId`, opening or RESUMING it as needed, and get told
 * where it stands. `onState` is called synchronously with the current state on subscribe, and
 * again on every change.
 *
 * The returned unsubscribe drops this observer. The LAST observer leaving cancels the cycle's
 * scheduling — never its accounting (see the header).
 *
 * The key is DERIVED here rather than accepted as a parameter. It used to take both, which let a
 * caller pass a key and a set that disagreed and so read another question's candidates under this
 * question's bound — and because an existing record keeps its own `skus`, the disagreement would
 * have been silent. Deriving it removes the class instead of documenting it.
 *
 * PRECONDITION ON THE CALLER: `depotId` must be the depot the consumer is rendering at, so that
 * every observer of one question re-subscribes on the same commit when the depot changes. That is
 * what guarantees a cycle at a depot the app has left loses its observers rather than issuing at
 * a stale depot — `drive` cannot tell "the store has not caught up yet" (legitimate: a child
 * effect is normally the first caller at a new depot) from "this cycle's depot is gone", because
 * both are just a mismatch. Today's only consumer takes it from `useSessionDepotId()`.
 */
export function observeReadCycle(
  skus: readonly string[],
  depotId: string,
  onState: Observer,
): () => void {
  const key = readCycleKey(JSON.stringify(skus), depotId);
  const existing = cycles.get(key);
  const cycle: Cycle = existing ?? {
    key,
    skus: [...skus],
    depotId,
    state: 'pending',
    observers: new Set<Observer>(),
    timer: undefined,
    inFlight: false,
  };
  if (existing === undefined) cycles.set(key, cycle);

  cycle.observers.add(onState);
  onState(cycle.state);
  drive(cycle);

  return () => {
    cycle.observers.delete(onState);
    if (cycle.observers.size > 0) return;
    if (cycle.timer !== undefined) {
      clearTimeout(cycle.timer);
      cycle.timer = undefined;
    }
    collect(cycle);
  };
}

/**
 * Test/session hygiene — drop every live cycle and the whole bound.
 *
 * The store's own `reset()` does NOT reach this (it is a separate seam and nothing in
 * application code calls it), so a suite that resets one must reset the other. A request already
 * on the wire when this runs is discarded by the identity check in `settle`.
 *
 * PRECONDITION: no consumer may outlive this call. Observers hold a closure over the record this
 * discards, so their unsubscribes no-op (the identity check in `collect`) and they never
 * re-subscribe — a consumer still mounted across a reset is stranded at whatever state it last
 * saw. Pair it with unmounting the consumers (a spec's `cleanup()`), which is why this is test and
 * session hygiene and not a runtime control.
 */
export function resetAvailabilityReadCycles(): void {
  for (const cycle of cycles.values()) {
    if (cycle.timer !== undefined) clearTimeout(cycle.timer);
  }
  cycles.clear();
  register.clear();
}
